/**
 * 서버/API에서 오는 시작일·종료일 등 **날짜만 의미하는 문자열**을
 * 브라우저 로컬 TZ 기준 자정으로 맞춤.
 *
 * `new Date('YYYY-MM-DD')`는 ES5 이후 스펙상 UTC 자정으로 파싱되어
 * 한국처럼 UTC+9 환경에서는 로컬 09:00으로 잡혀 당일 09:00 이전에는
 * "어제"처럼 비교되어 권한/상태가 틀어질 수 있음.
 */

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 오늘(로컬)의 자정 Date */
export function startOfTodayLocal(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** 로컬 캘린더 기준 내일의 `YYYY-MM-DD` (제한형 관리자 슬롯 생성 시 최소 시작일) */
export function minStartDateYmdForLimitedPartnerCreate(): string {
  const d = startOfTodayLocal();
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * 파싱 실패 시 `null`.
 * `YYYY-MM-DD` → 해당 일의 로컬 자정.
 * 그 외(ISO 시간 포함 등) → `Date` 후 로컬 캘린더 기준 자정으로 정규화.
 */
export function parseCalendarDate(input: string | null | undefined): Date | null {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim();

  const m = YMD_RE.exec(trimmed);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const day = Number(m[3]);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(day)) return null;
    const d = new Date(y, mo - 1, day);
    // 오버플로(잘못된 일자) 검증
    if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== day) return null;
    return d;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

export function isSameLocalCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export const calcRemaining = (end: string) => {
  const today = startOfTodayLocal();
  const endDate = parseCalendarDate(end);
  if (!endDate) return 0;

  const diffTime = endDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

export const isEndBeforeStart = (start?: string, end?: string): boolean => {
  if (!start || !end) return false;
  const s = parseCalendarDate(start);
  const e = parseCalendarDate(end);
  if (!s || !e) return false;
  return e.getTime() < s.getTime();
};
